import React, { useState, useEffect } from 'react';
import { Input, Button, Card, Typography, Alert, Space, Form, Select, Row, Col, message, Popover } from 'antd';
import { QuestionCircleOutlined, CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { VehicleFormData } from '../../types/vehicle';
import { useVehicleData } from '../../hooks/useVehicleData';

const { Title, Text } = Typography;
const { Option } = Select;

// Helper function to copy text to clipboard
const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    message.success('VIN copied to clipboard!');
  } catch (err) {
    message.error('Failed to copy VIN');
  }
};

export const VehicleDetailsReviewPage: React.FC<{
  docId: string;
  onNext: (vehicleData: VehicleFormData) => void;
  onBack: () => void;
}> = ({ docId, onNext, onBack }) => {
  const [form] = Form.useForm();
  const { vehicleData, loading, updateVehicleData, isManual } = useVehicleData(docId);
  const [saving, setSaving] = useState(false);
  const listingType = Form.useWatch('listingType', form);
  
  // Update form when vehicle data changes
  useEffect(() => {
    if (vehicleData) {
      form.setFieldsValue(vehicleData);
    }
  }, [vehicleData, form]);

  const handleSubmit = async (values: any) => {
    setSaving(true);
    try {
      await updateVehicleData(values);
      message.success('Vehicle details added successfully');
      // Pass the current vehicle data to parent for vital field checking
      onNext(values);
    } catch (err) {
      message.error('Failed to add vehicle details');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !vehicleData) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Title level={4}>Loading vehicle details...</Title>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <Title level={3}>{isManual ? 'Enter Vehicle Details' : 'Review and Confirm'}</Title>
      <Text type="secondary">
        {isManual
          ? 'Enter as much detail as you can. More details will result in a more accurate inspection report.'
          : "We've auto-filled this info from the listing. Please review and make any corrections before continuing."}
      </Text>
      <Alert
          message="💡 These details will power your AI inspection report, so accuracy matters."
          type="warning"
          style={{ marginTop: 24 }}
              />
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={vehicleData || undefined}
        style={{ marginTop: 24 }}
      >
        <Row gutter={16}>
          <Col xs={8} md={8} lg={8}>
            <Form.Item name="Make" label="Make">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={8} md={8} lg={8}>
            <Form.Item name="Model" label="Model">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={6} md={6} lg={2}>
            <Form.Item name="Year" label="Year">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={18} md={12} lg={8}>
            <Form.Item name="VIN" label="VIN">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={10} md={12} lg={8}>
            <Form.Item name="Mileage" label="Mileage">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={12} lg={8}>
            <Form.Item
              name="titleStatus"
              label={
                <Space>
                  Title Status
                  <Popover
                    content={
                      <div style={{ maxWidth: 300 }}>
                        <div>
                          <Text strong>Need help determining title status?</Text>
                          <br />
                          <Text>
                            Check if this vehicle has been reported stolen or salvaged using the NICB database. Copy the VIN below and paste it into the search bar on the NICB website.
                          </Text>
                          <br />
                          <br />
                          <Text >
                            💡 <strong>Tip:</strong> While NICB provides theft/salvage records, a vehicle history report (Carfax) offers more comprehensive title information.
                          </Text>
                        </div>
                        <div style={{ margin: '8px 0', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                          <Text strong>{vehicleData?.VIN || 'VIN not available! You should ask the seller for the VIN.'}</Text>
                        </div>
                        <Space>
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => copyToClipboard(vehicleData?.VIN || '')}
                            disabled={!vehicleData?.VIN}
                          >
                            Copy VIN
                          </Button>
                          <Button
                            size="small"
                            type="primary"
                            icon={<LinkOutlined />}
                            onClick={() => window.open('https://www.nicb.org/vincheck', '_blank')}
                          >
                            Check VIN
                          </Button>
                        </Space>
                      </div>
                    }
                    title="Title Status Help"
                    trigger="hover"
                  >
                    <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer', fontSize: '16px' }} />
                  </Popover>
                </Space>
              }
            >
              <Select>
                <Option value="Clean">Clean</Option>
                <Option value="Salvage">Salvage</Option>
                <Option value="Rebuilt">Rebuilt</Option>
                <Option value="Parts Only">Parts Only</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12} lg={8}>
            <Form.Item name="Engine" label="Engine">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Form.Item name="Transmission" label="Transmission">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Form.Item name="Drivetrain" label="Drivetrain">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={18} md={12} lg={8}>
            <Form.Item name="Trim" label="Trim">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Form.Item name="exteriorColor" label="Exterior Color">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Form.Item name="interiorColor" label="Interior Color">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={18} md={12} lg={8}>
            <Form.Item name="Location" label="Location">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={12} lg={8}>
            <Form.Item name="listingType" label="Listing Type">
              <Select placeholder="Select a listing type">
                <Option value="Auction">Auction</Option>
                <Option value="Classified">Classified</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={12} md={12} lg={8}>
            <Form.Item name="Price" label="Price">
              <Input disabled={listingType === 'Auction'} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={6} />
        </Form.Item>

        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button onClick={onBack} >
            Back
          </Button>
          <Button type="primary" htmlType="submit" loading={saving}>
            Confirm & Continue
          </Button>
        </Space>
      </Form>
    </Card>
  );
};
